import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPModal,
  IPPrimaryButton,
  IPSeparator,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import WarningMessage from 'components/Notification/WarningMessage';
import {
  globalStackStylesHeight100PaddingSmall,
  globalStackTokensGapSmall,
  refreshIcon,
  sharepointIcon,
} from 'globalStyles';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphScopeId } from 'services/Auth/authConfig';
import { getGraphSharepointErrorMsg, toAppError } from 'services/Graph/graphErrors';
import { graphGetListsForSite } from 'services/Graph/graphService';
import { navigateToExternalUrl } from 'utils/url';
import { IList, ISite } from '../../services/Graph/SharepointInterfaces';
import DirectoryParents from './DirectoryParents';
import MyLists from './MyLists';
import MySites from './MySites';

interface ISharepointListPicker {
  isOpen: boolean;
  close: () => void;
  onSelect: (item: IList) => void;
}

const SharepointListPicker = (props: ISharepointListPicker) => {
  const [selectedSite, setSelectedSite] = useState<ISite | undefined>(undefined);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [lists, setLists] = useState<IList[]>([]);
  const [selectedList, setSelectedList] = useState<IList | undefined>(undefined);
  const [spError, setSpError] = useState<string | undefined>(undefined);

  const appContext = useContext(AppContext);

  const { t } = useTranslation(['sharepoint', 'translation']);

  const checkToken = (token: string): boolean => {
    if (!token) {
      props.close();
      appContext.showNotification(t('translation:General.Notifications.TryAgainAfterConsent'), true);

      return false;
    }

    return true;
  };

  const fetchLists = async () => {
    if (isBusy || selectedSite === undefined || !selectedSite.id) return;
    try {
      setIsBusy(true);
      setSpError(undefined);
      // const accessToken: string = await appContext.getAccessToken(GraphScopeId.Pages);
      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, selectedSite.tenantId);
      if (!checkToken(graphInterface.accessToken)) return;
      const _lists: IList[] = await graphGetListsForSite(graphInterface.client, selectedSite.id);
      _lists.forEach((l) => (l.tenantId = selectedSite.tenantId));
      setLists(_lists);
    } catch (err) {
      const spError = getGraphSharepointErrorMsg(err, t);
      if (spError) {
        setSpError(spError);
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const updateSiteSelection = (site?: ISite) => {
    setSelectedSite(site);
  };

  useEffect(() => {
    fetchLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  const onRefresh = () => {
    //Refresh Lists for Group
    fetchLists();
  };

  const onOpenSharePoint = () => {
    if (!selectedSite?.webUrl) return;
    navigateToExternalUrl(selectedSite.webUrl + '/_layouts/15/viewlsts.aspx', undefined, '');
  };

  //
  // Main render
  //
  return (
    <IPModal isOpen={props.isOpen} onDismiss={props.close} isBlocking>
      <IPStack
        verticalFill
        styles={{ root: { height: '80vh', width: 1000, minWidth: 320, maxWidth: '90vw', padding: 20 } }}
      >
        {/* Title and Close button*/}
        <IPStack horizontal styles={{ root: { justifyContent: 'space-between' } }}>
          <IPStack.Item>
            <IPText variant="xLarge">
              {!selectedSite && t('sharepoint:SharepointPicker.SelectGroup')}
              {selectedSite && t('sharepoint:SharepointPicker.SelectList')}
            </IPText>
          </IPStack.Item>
          <IPStack.Item>
            <IPIconButton iconProps={{ iconName: 'CalculatorMultiply' }} onClick={() => props.close()} />
          </IPStack.Item>
        </IPStack>
        <IPSeparator />
        {/* Body Left and Right*/}
        <IPStack.Item grow>
          <IPStack horizontal styles={globalStackStylesHeight100PaddingSmall} tokens={globalStackTokensGapSmall}>
            {/* Left Groups Part*/}
            <IPStack.Item styles={{ root: { width: '30%' } }}>
              <MySites
                onSelect={(item) => updateSiteSelection(item)}
                selectedSite={selectedSite}
                checkToken={checkToken}
                authType={GraphScopeId.Pages}
              />
            </IPStack.Item>
            <IPSeparator vertical />
            {/* Right Part*/}
            <IPStack.Item grow styles={{ root: { width: '70%' } }}>
              <IPStack verticalFill>
                {selectedSite && (
                  <IPStack.Item>
                    <IPStack horizontal wrap horizontalAlign="space-between">
                      <IPStack.Item>
                        <DirectoryParents
                          drive={undefined}
                          parents={[]}
                          site={selectedSite}
                          onSelectDrive={() => {}}
                          onSelectDriveFolder={() => {}}
                          onBack={() => {}}
                          onSelectSite={updateSiteSelection}
                        />
                      </IPStack.Item>
                      <IPStack.Item>
                        {selectedSite && (
                          <IPTooltipHost content={t('sharepoint:CommandBar.OpenSharePoint')}>
                            <IPIconButton iconProps={sharepointIcon} onClick={onOpenSharePoint} />
                          </IPTooltipHost>
                        )}
                        <IPTooltipHost content={t('sharepoint:CommandBar.Refresh')}>
                          <IPIconButton iconProps={refreshIcon} onClick={onRefresh} />
                        </IPTooltipHost>
                      </IPStack.Item>
                    </IPStack>
                  </IPStack.Item>
                )}
                {spError && <WarningMessage message={spError} />}
                <IPStack.Item grow>
                  <MyLists
                    lists={lists}
                    onSelect={(item?: IList) => {
                      setSelectedList(item);
                    }}
                    isListLoading={isBusy}
                  />
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPSeparator />
        <IPStack horizontalAlign="end" horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            text={t('translation:General.Button.Select')}
            disabled={selectedList === undefined}
            onClick={() => {
              if (selectedList) {
                props.onSelect(selectedList);
                props.close();
              }
            }}
          />
          <IPDefaultButton text={t('translation:General.Button.Cancel')} onClick={() => props.close()} />
        </IPStack>
      </IPStack>
    </IPModal>
  );
};

export default SharepointListPicker;
